/**
 * The business month an MRR number belongs to.
 *
 * `mrr_snapshots` is keyed on `(month, source, account_id)` where `month` is a
 * `YYYY-MM` string in WTC's OPERATING timezone (America/Chicago), not UTC. Those
 * two disagree for the last five-to-six hours of every business day, which on
 * the last day of a month means a snapshot captured at 7pm Central lands in the
 * FOLLOWING month's bucket — a month with one stray row in it and a month
 * missing one, i.e. a phantom churn and a phantom new account in the same diff.
 *
 * Three copies of this key-derivation already existed (the capture cron, the
 * browser hook, and now a route). They agreed, which is luck rather than
 * design; this is the one copy.
 *
 * Deliberately NOT in `@west-tennessee-consulting/business-date`: that package answers "what calendar
 * day is it for the books" and is about invoice dates. A month BUCKET KEY for a
 * revenue series is a different question with a different consumer, and folding
 * it in would make an invoice-date module a dependency of every MRR reader.
 */

/** WTC's operating wall clock. Storage stays UTC; buckets do not. */
export const MRR_SNAPSHOT_TIME_ZONE = "America/Chicago";

const MONTH_KEY_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

/**
 * `YYYY-MM` for the business month `at` falls in.
 *
 * `en-CA` because it formats as `YYYY-MM-DD`, so the first seven characters are
 * the key with no re-assembly and no locale surprises.
 */
export function monthKey(at: Date, timeZone: string = MRR_SNAPSHOT_TIME_ZONE): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  })
    .format(at)
    .slice(0, 7);
}

/** Is this a well-formed `YYYY-MM` key? */
export function isMonthKey(value: string | null | undefined): boolean {
  return typeof value === "string" && MONTH_KEY_RE.test(value);
}

/**
 * The key `count` months before `key`.
 *
 * Pure string/integer arithmetic on the KEY rather than `Date.setMonth` on an
 * instant: `setMonth` on the 31st of a month rolls into the next one (Mar 31 −
 * 1 month = Mar 3), which would silently return the wrong prior month on eight
 * days of the year. The bug is invisible for 23 days a month and then produces
 * a diff against the wrong baseline.
 */
export function previousMonthKey(key: string, count = 1): string {
  if (!isMonthKey(key)) {
    throw new Error(`previousMonthKey: "${key}" is not a YYYY-MM month key`);
  }
  if (!Number.isInteger(count)) {
    throw new Error(`previousMonthKey: count must be an integer, got ${count}`);
  }
  const [yearPart, monthPart] = key.split("-");
  const absolute = Number(yearPart) * 12 + (Number(monthPart) - 1) - count;
  const year = Math.floor(absolute / 12);
  const month = absolute - year * 12 + 1;
  return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}`;
}

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

/**
 * "September 2026" — the label for a month key.
 *
 * Built from the key's own digits rather than by constructing a `Date` and
 * formatting it: `new Date("2026-09")` is parsed as UTC midnight, which renders
 * as August in every timezone west of Greenwich. That is the same off-by-one
 * this module exists to prevent, and it would land on the one string a person
 * actually reads.
 */
export function formatMonthLabel(key: string): string {
  if (!isMonthKey(key)) return key;
  const [yearPart, monthPart] = key.split("-");
  return `${MONTH_NAMES[Number(monthPart) - 1]} ${yearPart}`;
}
